import { FileActionButton } from "../file-action/file-action-controls";
import React, { useState, useEffect, Suspense } from 'react';
import { Toast } from 'primereact/toast';
import ProductBatchService from '../../services/productbatch/productBatchService';
import { ProductBatchExpireMetadata, ProductExpiryStatusExportFilterDTO, ProductExpiryStatusFilterDTO, ProductExpiryStatusResponseDTO } from "../../models/productbatch-model";
import ProductBatchExpireSearch from './ProductBatchExpireSearch';
import ProductBatchExpireDataTable from './ProductBatchExpireDataTable';
import { useRef } from 'react';
import styles from './styles/productbatchexpire-header.module.css';
import AppLoading from '../app-loading/app-loading';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ProductBatchExpireHeader: React.FC = () => {
  const toast = useRef<Toast>(null);
  
  const [metadata, setMetadata] = useState<ProductBatchExpireMetadata | null>(null);
  const [expiryData, setExpiryData] = useState<ProductExpiryStatusResponseDTO>({
    totalRecords: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 0,
    data: [],
  });
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<0 | 1 | -1 | null | undefined>(undefined);
  const [currentFilters, setCurrentFilters] = useState<ProductExpiryStatusFilterDTO>({
    posId: null,
    productId: null,
    categoryId: null,
    batchNo: null,
    expiryDateFrom: null,
    expiryDateTo: null,
    expiresInDaysFrom: null,
    expiresInDaysTo: null,
    correntStockFlag: 0,
    pageNumber: 1,
    pageSize: 10,
    sortColumn: null,
    sortDirection: null,
  });
  const [hasSearched, setHasSearched] = useState(false);


  // Load metadata on component mount
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        setIsLoadingMetadata(true);
        const data = await ProductBatchService.getProductBatchExpireMetadata();
        setMetadata(data);
      } catch (error) {
        console.error('Error loading metadata:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load filter options',
          life: 3000,
        });
      } finally {
        setIsLoadingMetadata(false);
      }
    };

    loadMetadata();
  }, []);

  // Load initial data
  useEffect(() => {
    if (metadata) {
      setHasSearched(true);
      loadExpiryData(currentFilters);
    }
  }, [metadata]);

  const loadExpiryData = async (filters: ProductExpiryStatusFilterDTO) => {
    try {
      setIsLoadingData(true);
      const response = await ProductBatchService.getProductExpiryStatus(filters);
      setExpiryData(response);
      setCurrentFilters(filters);
    } catch (error) {
      console.error('Error loading expiry data:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load product expiry data',
        life: 3000,
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleSearch = (filters: ProductExpiryStatusFilterDTO) => {
    setHasSearched(true);
    setSortField(undefined);
    setSortOrder(undefined);
    loadExpiryData(filters);
  };

  const handlePageChange = (pageNumber: number, pageSize: number) => {
    const updatedFilters = {
      ...currentFilters,
      pageNumber,
      pageSize,
    };
    loadExpiryData(updatedFilters);
  };

  const handleSort = (sortColumn: string, sortDirection: string) => {
    const newSortOrder = sortDirection === 'ASC' ? 1 : -1;
    setSortField(sortColumn);
    setSortOrder(newSortOrder);
    const updatedFilters = {
      ...currentFilters,
      sortColumn,
      sortDirection,
      pageNumber: 1,
    };
    loadExpiryData(updatedFilters);
  };

  const handleRefresh = () => {
    loadExpiryData(currentFilters);
  };

  const buildExportFilters = (): ProductExpiryStatusExportFilterDTO => {
  return {
    posId: currentFilters.posId,
    productId: currentFilters.productId,
    categoryId: currentFilters.categoryId,
    batchNo: currentFilters.batchNo,
    expiryDateFrom: currentFilters.expiryDateFrom,
    expiryDateTo: currentFilters.expiryDateTo,
    expiresInDaysFrom: currentFilters.expiresInDaysFrom,
    expiresInDaysTo: currentFilters.expiresInDaysTo,
    correntStockFlag: currentFilters.correntStockFlag,
    sortColumn: currentFilters.sortColumn,
    sortDirection: currentFilters.sortDirection,
  };
};

const handleExport = async () => {
  try {
    setIsLoadingData(true);

    const exportFilters = buildExportFilters();

    const response =
      await ProductBatchService.getExportProductExpiryStatus(exportFilters);

    const exportData = response.data;

    if (!exportData || exportData.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "No Data",
        detail: "No records available to export",
        life: 3000,
      });
      return;
    }

    const worksheetData = exportData.map((item) => {
        const days = item.expiresInDays;

        let expiresText = "";
        if (days < 0) {
          expiresText = `${Math.abs(days)} days ago`;
        } else {
          expiresText = `${days} days`;
        }

        let statusText = "";
        if (days < 0) {
          statusText = "Expired";
        } else if (days === 0) {
          statusText = "Today";
        } else if (days <= 30) {
          statusText = `Expiring in ${days} days`;
        } else {
          statusText = "Normal";
        }

    return {
      "POS Code": item.posCode,
      "Product Name": item.productName,
      "Category": item.category,
      "Batch No": item.batchNo,
      "Current Stock": item.currentStock,
      "Manufacture Date": item.manufactureDate
        ? new Date(item.manufactureDate).toLocaleDateString("en-GB")
        : "",
      "Expiry Date": item.expiryDate
        ? new Date(item.expiryDate).toLocaleDateString("en-GB")
        : "",
      "Expires In Days": expiresText,
       "Status": statusText,
    };
  });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Product Expiry");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "ProductBatchExpiry.xlsx");

    toast.current?.show({
      severity: "success",
      summary: "Exported",
      detail: `Exported successfully`,
      life: 3000,
    });
  } catch (error) {
    toast.current?.show({
      severity: "error",
      summary: "Export Failed",
      detail: "Unable to export data",
      life: 3000,
    });
  } finally {
    setIsLoadingData(false);
  }
};

  return (
    <Suspense fallback={<AppLoading />}>
      <Toast ref={toast} />
      
      {/* Header Section */}
   <div className="layout-content">
  <div className={styles.expiryHeaderCard}>
    <div className="flex align-items-center justify-content-between">
      
      {/* LEFT SIDE — Title */}
      <div className={styles.expiryTitleWrapper}>
        <div className={styles.expiryIconWrapper}>
          <i className="pi pi-box"></i>
        </div>
        <div>
          <h1 className={styles.expiryHeaderTitle}>
            Product Batch Expiry Monitor
          </h1>
          <p className={styles.expiryHeaderSubtitle}>
            Track and manage product batch expiry dates
          </p>
        </div>
      </div>

      <FileActionButton action="export"
        label="Export"
        icon="pi pi-download"
        className={styles.exportExpiryBtn}
        loading={isLoadingData}
        onClick={handleExport}
        disabled={expiryData.data.length === 0}
      />
      
    </div>
  </div>
</div>

      {/* Search Section */}
      <div className="layout-content" style={{ marginTop: '1.5rem' }}>
        {isLoadingMetadata ? (
          <AppLoading />
        ) : (
          <ProductBatchExpireSearch
            metadata={metadata}
            onSearch={handleSearch}
            isLoading={isLoadingData}
          />
        )}
      </div>

      {/* DataTable Section */}
      <div className="layout-content" style={{ marginTop: '1.5rem' }}>
                {isLoadingData ? (
            <AppLoading />
          ) : hasSearched ? (
            <ProductBatchExpireDataTable
              data={expiryData}
              isLoading={isLoadingData}
              sortField={sortField}
              sortOrder={sortOrder}
              onPageChange={handlePageChange}
              onSort={handleSort}
              onRefresh={handleRefresh}
            />
          ) : (
            <div className="col-12 text-center p-4">
              <p>Please apply filters to view product batch expiry</p>
          </div>
        )}
      </div>
    </Suspense>
  );
};

export default ProductBatchExpireHeader;
